import { useEffect, useMemo, useState } from "react";
import { User, Sparkles, Users2, X } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { API_URL } from "../../lib/utils";

export const Team = () => {
  const DEFAULT_TEAM = useMemo(
    () => [
      {
        id: "maryann_hope",
        name: "Maryann Hope",
        role: "Finance & Compliance Coordinator",
        location: "Rivers State, Nigeria",
        image_url: "/assets/team/maryann_hope.png",
        short:
          "Oversees financial stewardship, compliance, and reporting to keep GLGIF accountable and transparent.",
        bio:
          "Maryann supports GLGIF’s financial integrity by managing records, budgeting support, and compliance processes that protect the organization and its beneficiaries. She helps ensure funds are properly documented, program spending is aligned with approved plans, and reporting is clear for donors and partners. Her work strengthens trust, transparency, and responsible service delivery across GLGIF operations—especially in field-based interventions and community programs.",
        highlights: [
          "Financial documentation & basic reporting support",
          "Compliance checks for program spending",
          "Supports budgeting and accountability workflows",
          "Improves transparency for partners & donors",
        ],
      },
      {
        id: "gold_blessing_idem",
        name: "Edo-bong Clement",
        role: "Executive Director & Founder",
        location: "GLGIF",
        image_url: "/assets/team/gold_blessing_idem.png",
        short:
          "Leads GLGIF’s mission, partnerships, and strategy—driving people-centered programs with measurable impact.",
        bio:
          "Edo-bong Clement is the Executive Director and Founder of GLGIF. She provides strategic leadership, builds partnerships, and guides the organization’s vision across child protection, humanitarian response, empowerment, and climate-resilient livelihoods. She works closely with community leaders and stakeholders to design practical interventions that restore dignity, strengthen families, and create long-term stability for vulnerable people.",
        highlights: [
          "Strategic leadership & vision execution",
          "Program design & community partnerships",
          "Safeguarding and systems strengthening",
          "Impact planning and accountability",
        ],
        isFounder: true,
      },
      {
        id: "joy_zulu",
        name: "Joy Zulu",
        role: "International Coordinator",
        location: "Zambia • Monitoring, Evaluation & Documentation",
        image_url: "/assets/team/joy_zulu.png",
        short:
          "Coordinates Zambia activities and strengthens results through monitoring, evaluation, and documentation.",
        bio:
          "Joy supports GLGIF’s international coordination in Zambia and ensures programs are well-tracked, well-documented, and outcomes-focused. She supports field coordination, monitoring visits, evidence gathering, and reporting that helps GLGIF learn, improve, and demonstrate impact. Her attention to documentation and measurement strengthens credibility and helps ensure every intervention is purposeful and accountable.",
        highlights: [
          "International coordination (Zambia)",
          "Monitoring & evaluation support",
          "Documentation, stories & reporting",
          "Helps track outcomes and lessons learned",
        ],
      },
      {
        id: "treasure_idem",
        name: "Treasure Idem",
        role: "Volunteer • Community Engagements • Communications",
        location: "GLGIF",
        image_url: "/assets/team/treasure_idem.png",
        short:
          "Supports volunteer mobilization, community relationships, and communications that amplify GLGIF’s work.",
        bio:
          "Treasure supports GLGIF’s volunteer network and community engagement, helping to coordinate outreach, mobilize support, and strengthen relationships with people on the ground. He contributes to communications and storytelling—capturing what matters, celebrating impact, and helping communities feel seen and supported. His work helps connect GLGIF’s mission to real people through clarity, consistency, and compassion.",
        highlights: [
          "Volunteer mobilization & support",
          "Community engagement and outreach",
          "Communications & storytelling support",
          "Helps strengthen public trust and visibility",
        ],
      },
      {
        id: "mfoniso_godwin",
        name: "Mfoniso Godwin",
        role: "Programs & Operations Lead",
        location: "Akwa Ibom State Chapter Coordinator",
        image_url: "/assets/team/mfonso_godwin.png",
        short:
          "Leads program delivery and operations while coordinating GLGIF’s Akwa Ibom State chapter activities.",
        bio:
          "Mfoniso supports GLGIF’s programs and operations by helping plan, coordinate, and execute projects efficiently. As Akwa Ibom State Chapter Coordinator, she helps organize local activities, field teams, and community partners to ensure interventions run smoothly. She is focused on practical delivery—turning plans into action, supporting beneficiaries, and ensuring programs meet real needs with structure and accountability.",
        highlights: [
          "Programs planning & field coordination",
          "Operations support and execution",
          "Akwa Ibom State Chapter leadership",
          "Helps ensure smooth, timely delivery",
        ],
      },
      {
        id: "nseabasi_isaac",
        name: "NseAbasi Isaac",
        role: "Project Lead",
        location: "Akwa Ibom State, Nigeria",
        image_url: "/assets/team/nseabasi_isaac.png",
        short:
          "Leads project planning, coordination, and implementation to ensure impactful and sustainable community programs.",
        bio:
          "NseAbasi leads the planning, coordination, and execution of GLGIF projects, ensuring that programs are delivered effectively and create meaningful impact in the communities they serve. She works closely with stakeholders, team members, volunteers, and partners to drive project success from concept to completion. Her leadership helps ensure that organizational goals are translated into measurable outcomes while maintaining quality, accountability, and sustainability across all initiatives.",
        highlights: [
          "Leads project planning, implementation, and monitoring",
          "Coordinates project teams, volunteers, and partners",
          "Ensures project activities align with organizational objectives",
          "Oversees timelines, deliverables, and resource allocation",
          "Tracks project performance and impact outcomes",
          "Supports stakeholder engagement and partnership development",
          "Identifies risks and implements effective mitigation strategies",
          "Ensures quality, accountability, and sustainability in project delivery",
          "Prepares project reports and progress updates for management and partners",
          "Facilitates effective communication across project stakeholders",
        ],
      },
    ],
    []
  );

  const [teamMembers, setTeamMembers] = useState(DEFAULT_TEAM);
  const [loading, setLoading] = useState(true);
  const [openMember, setOpenMember] = useState(null);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await fetch(`${API_URL}/team`);
        const data = await response.json();

        if (Array.isArray(data) && data.length > 0) {
          setTeamMembers(data);
        } else {
          setTeamMembers(DEFAULT_TEAM);
        }
      } catch (error) {
        console.error("Error fetching team:", error);
        setTeamMembers(DEFAULT_TEAM);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [DEFAULT_TEAM]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenMember(null);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const founder = useMemo(() => {
    return teamMembers.find(
      (m) =>
        m.isFounder ||
        String(m.role || "")
          .toLowerCase()
          .includes("founder")
    );
  }, [teamMembers]);

  const coreTeam = useMemo(() => {
    return teamMembers.filter((m) => m !== founder);
  }, [teamMembers, founder]);

  const MoreBtn = ({ onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-full bg-stone-900 text-white px-4 py-2 text-xs font-semibold hover:bg-stone-800 transition"
    >
      + More
    </button>
  );

  return (
    <div data-testid="team-page" className="bg-white text-stone-900">
      <section className="relative overflow-hidden py-24 md:py-36 text-white">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/team-hero.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-stone-950/90 via-blue-950/75 to-black/70" />
        <div className="absolute -top-20 -left-20 w-[280px] h-[280px] rounded-full bg-amber-400/20 blur-3xl" />
        <div className="absolute -bottom-20 -right-10 w-[260px] h-[260px] rounded-full bg-cyan-400/15 blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur-sm mb-6">
              <Sparkles className="w-4 h-4 text-amber-300" />
              About Us
            </span>

            <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Our Team
            </h1>

            <p className="text-lg md:text-xl text-stone-200 max-w-2xl leading-relaxed">
              GLGIF is powered by compassionate leaders, volunteers, and partners committed to
              people-centered impact.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {founder && (
              <div className="lg:col-span-5">
                <Card className="rounded-2xl border-stone-200/80 shadow-sm overflow-hidden bg-white">
                  <div className="aspect-[4/3] bg-gradient-to-b from-stone-100 to-stone-50">
                    {founder.image_url ? (
                      <img
                        src={founder.image_url}
                        alt={founder.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center border border-amber-200">
                          <User className="w-11 h-11 text-amber-600" />
                        </div>
                      </div>
                    )}
                  </div>

                  <CardContent className="p-6 text-center">
                    <p className="text-xs font-semibold tracking-wide uppercase text-stone-500 mb-2">
                      Founder
                    </p>
                    <h3 className="font-heading text-xl font-bold text-stone-900 mb-1">
                      {founder.name}
                    </h3>
                    <p className="text-amber-700 text-sm font-medium mb-2">{founder.role}</p>

                    <div className="flex items-center justify-center">
                      <MoreBtn onClick={() => setOpenMember(founder)} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className={founder ? "lg:col-span-7" : "lg:col-span-12"}>
              <p className="font-accent text-xl text-amber-700 mb-4 text-center lg:text-left">
                A Message From the Founder
              </p>

              <p className="text-lg text-stone-700 leading-relaxed text-center lg:text-left">
                GLGIF was born from one simple conviction: vulnerable people deserve more than
                sympathy—they deserve protection, opportunity, and dignity. We may not solve every
                problem at once, but we can show up consistently, listen deeply, and build practical
                solutions with communities. Our work is driven by compassion, guided by
                accountability, and strengthened by partnerships that believe impact must be
                measurable and long-lasting.
              </p>

              <p className="text-lg text-stone-700 leading-relaxed mt-4 text-center lg:text-left">
                Every program we deliver is a promise: to serve with integrity, to prioritize the
                vulnerable, and to create pathways that help families become stable, resilient, and
                empowered. Thank you for believing in this mission—and for standing with the people
                we serve.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Users2 className="w-5 h-5" />
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">
              Core Team
            </h2>
          </div>

          <div className="h-px bg-gradient-to-r from-stone-200 via-stone-300/70 to-transparent mb-8" />

          {loading ? (
            <div className="flex flex-wrap justify-center gap-6">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] rounded-2xl border border-stone-200 bg-white p-4 shadow-sm"
                >
                  <div className="aspect-square rounded-xl bg-stone-100 animate-pulse mb-4" />
                  <div className="h-4 w-3/4 bg-stone-100 rounded animate-pulse mb-2" />
                  <div className="h-3 w-1/2 bg-stone-100 rounded animate-pulse mb-3" />
                  <div className="h-3 w-full bg-stone-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-6">
              {coreTeam.map((member, index) => (
                <Card
                  key={member.id || `core-${index}`}
                  className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] rounded-2xl border-stone-200/80 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all overflow-hidden bg-white"
                  data-testid={`team-member-${index}`}
                >
                  <div className="aspect-square bg-gradient-to-b from-stone-100 to-stone-50 flex items-center justify-center">
                    {member.image_url ? (
                      <img
                        src={member.image_url}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center border border-amber-200">
                        <User className="w-11 h-11 text-amber-600" />
                      </div>
                    )}
                  </div>

                  <CardContent className="p-5 text-center">
                    <h3 className="font-heading text-lg font-bold text-stone-900 mb-1">
                      {member.name}
                    </h3>
                    <p className="text-amber-700 text-sm font-medium mb-1">{member.role}</p>

                    {member.location && (
                      <p className="text-stone-500 text-xs mb-2">{member.location}</p>
                    )}

                    {member.short && (
                      <p className="text-stone-600 text-sm leading-relaxed line-clamp-3">
                        {member.short}
                      </p>
                    )}

                    <div className="mt-4 flex justify-center">
                      <MoreBtn onClick={() => setOpenMember(member)} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-blue-950 via-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-5">Join Our Team</h2>
          <p className="text-lg text-blue-200 mb-8 leading-relaxed">
            Want to be part of this movement? We are always looking for passionate individuals who
            share our vision of empowering lives and driving impact.
          </p>
          <a
            href="/get-involved#volunteer"
            className="inline-flex items-center justify-center bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-4 font-medium transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            data-testid="join-team-btn"
          >
            Become a Volunteer
          </a>
        </div>
      </section>

      {openMember && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Details for ${openMember.name}`}
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            onClick={() => setOpenMember(null)}
            aria-label="Close modal"
          />

          <div className="relative z-[61] w-full max-w-3xl rounded-2xl bg-white shadow-2xl overflow-hidden max-h-[86vh]">
            <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200">
              <div className="min-w-0">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-stone-900 truncate">
                  {openMember.name}
                </h3>
                <p className="text-amber-700 text-sm font-medium">{openMember.role}</p>

                {openMember.location && (
                  <p className="text-stone-500 text-sm mt-1">{openMember.location}</p>
                )}
              </div>

              <button
                type="button"
                onClick={() => setOpenMember(null)}
                className="p-2 rounded-full hover:bg-stone-100 transition"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-stone-700" />
              </button>
            </div>

            <div className="grid md:grid-cols-5 overflow-hidden">
              <div className="md:col-span-2 bg-stone-50">
                <div className="h-[220px] md:h-full md:min-h-[360px]">
                  {openMember.image_url ? (
                    <img
                      src={openMember.image_url}
                      alt={openMember.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center border border-amber-200">
                        <User className="w-11 h-11 text-amber-600" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="md:col-span-3 p-6 overflow-y-auto max-h-[calc(86vh-72px)]">
                {openMember.bio && (
                  <p className="text-stone-700 leading-relaxed">{openMember.bio}</p>
                )}

                {Array.isArray(openMember.highlights) && openMember.highlights.length > 0 && (
                  <div className="mt-5">
                    <p className="text-sm font-semibold text-stone-900 mb-3">
                      Key Responsibilities
                    </p>
                    <ul className="space-y-2">
                      {openMember.highlights.map((h, i) => (
                        <li key={i} className="flex gap-2 text-sm text-stone-700">
                          <span className="mt-1.5 h-2 w-2 rounded-full bg-amber-500 shrink-0" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-6 pt-5 border-t border-stone-200 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setOpenMember(null)}
                    className="inline-flex items-center justify-center rounded-full bg-stone-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-stone-800 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;